import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import eiaService from '../../services/eiaService';
import projectService from '../../services/projectService';
import DatePickerField from '../../components/common/DatePickerField';
import '../monitoring/Monitoring.css';

const emptyForm = {
  projectId: '',
  certificateNumber: '',
  issuingAuthority: '',
  countryCode: '',
  approvalDate: '',
  expiryDate: '',
};

/**
 * Register/list formal EIA Certificates and the org-configurable Conditions
 * tracked under each one (see EIACertificateDetail.js). This is the
 * structured, rule-based counterpart to the free-form Inspection/
 * ComplianceFinding path used elsewhere - orgs that have an official EIA
 * approval to track use this; orgs that don't can keep using Inspections.
 */
const EIACertificates = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState('all');
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const fetchProjects = useCallback(async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, []);

  const fetchCertificates = useCallback(async () => {
    try {
      setLoading(true);
      const params = projectFilter !== 'all' ? { projectId: projectFilter } : {};
      const data = await eiaService.getCertificates(params);
      setCertificates(data.certificates || []);
    } catch (error) {
      console.error('Failed to fetch EIA certificates:', error);
      toast.error(error.response?.data?.error || 'Failed to load EIA certificates');
    } finally {
      setLoading(false);
    }
  }, [projectFilter]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchCertificates(); }, [fetchCertificates]);

  const openCreateModal = () => {
    const defaultProjectId = projectFilter !== 'all' ? projectFilter : (projects[0]?.id || '');
    const defaultProject = projects.find((p) => p.id === defaultProjectId);
    setFormData({ ...emptyForm, projectId: defaultProjectId, countryCode: defaultProject?.countryCode || '' });
    setShowCreateModal(true);
  };

  const handleFieldChange = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Default the certificate's country to the selected project's country
      // (the org can still override it for cross-border projects).
      if (field === 'projectId') {
        const project = projects.find((p) => p.id === value);
        if (project?.countryCode) next.countryCode = project.countryCode;
      }
      return next;
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.projectId || !formData.certificateNumber || !formData.issuingAuthority || !formData.countryCode) {
      toast.error('Project, certificate number, issuing authority and country are required');
      return;
    }
    try {
      setCreating(true);
      const { certificate } = await eiaService.createCertificate(formData);
      toast.success('EIA certificate registered successfully');
      setShowCreateModal(false);
      navigate(`/eia/certificates/${certificate.id}`);
    } catch (error) {
      console.error('Failed to register EIA certificate:', error);
      toast.error(error.response?.data?.error || 'Failed to register EIA certificate');
    } finally {
      setCreating(false);
    }
  };

  const stats = {
    total: certificates.length,
    active: certificates.filter((c) => c.status === 'active').length,
    expired: certificates.filter((c) => c.status === 'expired' || c.status === 'revoked').length,
    conditions: certificates.reduce((sum, c) => sum + (c.conditions?.length || 0), 0),
  };

  return (
    <div className="monitoring-page">
      <div className="monitoring-header">
        <div>
          <h1><i className="fas fa-file-contract"></i> EIA Certificates</h1>
          <p className="header-subtitle">
            Register formal Environmental Impact Assessment approvals and the conditions your organisation
            must track under each one.
          </p>
        </div>
        <button className="btn-primary-green" onClick={openCreateModal}>
          <i className="fas fa-plus"></i> Register EIA Certificate
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon"><i className="fas fa-file-contract"></i></div>
          <div><div className="stat-label">Total Certificates</div><div className="stat-value">{stats.total}</div></div>
        </div>
        <div className="stat-card success">
          <div className="stat-icon"><i className="fas fa-check-circle"></i></div>
          <div><div className="stat-label">Active</div><div className="stat-value">{stats.active}</div></div>
        </div>
        <div className="stat-card warning">
          <div className="stat-icon"><i className="fas fa-exclamation-triangle"></i></div>
          <div><div className="stat-label">Expired / Revoked</div><div className="stat-value">{stats.expired}</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-list-check"></i></div>
          <div><div className="stat-label">Conditions Tracked</div><div className="stat-value">{stats.conditions}</div></div>
        </div>
      </div>

      <div className="monitoring-controls">
        <select className="filter-select" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
          <option value="all">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading EIA certificates...</h3>
        </div>
      ) : certificates.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-file-contract"></i>
          <h3>No EIA certificates registered yet</h3>
          <p>Register a certificate to start tracking its conditions with auto-generated monitoring tasks.</p>
          <button className="btn-text" onClick={openCreateModal}>
            <i className="fas fa-plus"></i> Register EIA Certificate
          </button>
        </div>
      ) : (
        <div className="monitoring-list">
          {certificates.map((certificate) => (
            <div
              key={certificate.id}
              className="monitoring-card"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/eia/certificates/${certificate.id}`)}
            >
              <div className="monitoring-card-main">
                <h4>{certificate.certificateNumber}</h4>
                <div className="monitoring-card-meta">
                  <span><i className="fas fa-project-diagram"></i> {certificate.project?.name || 'Project'}</span>
                  <span><i className="fas fa-university"></i> {certificate.issuingAuthority}</span>
                  <span><i className="fas fa-globe"></i> {certificate.countryCode}</span>
                  <span><i className="fas fa-list-check"></i> {certificate.conditions?.length || 0} condition{(certificate.conditions?.length || 0) === 1 ? '' : 's'}</span>
                  {certificate.expiryDate && (
                    <span><i className="fas fa-calendar-times"></i> Expires {new Date(certificate.expiryDate).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              <span className={`status-badge status-${certificate.status}`}>{certificate.status}</span>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="monitoring-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-file-contract"></i> Register EIA Certificate</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-content">
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Project *</label>
                    <select value={formData.projectId} onChange={handleFieldChange('projectId')} required>
                      <option value="">Select a project...</option>
                      {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Certificate Number *</label>
                    <input
                      type="text"
                      value={formData.certificateNumber}
                      onChange={handleFieldChange('certificateNumber')}
                      placeholder="e.g. NEMA/EIA/2026/0123"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Issuing Authority *</label>
                    <input
                      type="text"
                      value={formData.issuingAuthority}
                      onChange={handleFieldChange('issuingAuthority')}
                      placeholder="e.g. NEMA"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Country Code *</label>
                    <input
                      type="text"
                      value={formData.countryCode}
                      onChange={handleFieldChange('countryCode')}
                      placeholder="e.g. UG"
                      maxLength={2}
                      style={{ textTransform: 'uppercase' }}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Approval Date</label>
                    <DatePickerField
                      value={formData.approvalDate}
                      onChange={handleFieldChange('approvalDate')}
                      maxDate={new Date().toISOString().slice(0, 10)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Expiry Date</label>
                    <DatePickerField
                      value={formData.expiryDate}
                      onChange={handleFieldChange('expiryDate')}
                      minDate={formData.approvalDate}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary-green" disabled={creating}>
                  {creating ? 'Registering...' : 'Register Certificate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EIACertificates;
